import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Stepper } from '@/components/ui/stepper'
import { WizardNavigation } from '@/components/PlanWizard/WizardNavigation'
import {
  prepareCommitmentLetter,
  type CommitmentLetterInputs,
} from '@/api/commitmentLetters'
import { useCommitmentLetterExport } from '@/hooks/useCommitmentLetterExport'
import type { PlanDetailItem } from '@/types'
import {
  commitmentLetterSchema,
  defaultFormValues,
  STEP_FIELDS,
  toInputs,
  type CommitmentLetterFormInput,
} from './schema'
import { StepDetails } from './StepDetails'
import { StepContacts } from './StepContacts'
import { StepTransit } from './StepTransit'
import { StepReview } from './StepReview'

const STEPS = [
  { id: 'details', label: 'Details' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'transit', label: 'Ports & Transit' },
  { id: 'review', label: 'Review' },
]
const TRANSIT_STEP = 2
const REVIEW_STEP = 3

interface CommitmentLetterWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId?: string
  selectedItems: PlanDetailItem[]
  onClearSelection: () => void
}

export function CommitmentLetterWizard({
  open,
  onOpenChange,
  planId,
  versionId,
  selectedItems,
  onClearSelection,
}: CommitmentLetterWizardProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [acknowledgedMissing, setAcknowledgedMissing] = useState(false)
  const [reviewInputs, setReviewInputs] =
    useState<CommitmentLetterInputs | null>(null)
  const [defaultsApplied, setDefaultsApplied] = useState(false)

  const form = useForm<CommitmentLetterFormInput>({
    resolver: zodResolver(
      commitmentLetterSchema
    ) as unknown as Resolver<CommitmentLetterFormInput>,
    defaultValues: defaultFormValues(),
    mode: 'onTouched',
  })

  const lineItemIds = useMemo(
    () => selectedItems.map(item => item.id),
    [selectedItems]
  )

  const strategyCodes = form.watch('strategyCodes')

  const prepare = useQuery({
    queryKey: [
      'commitment-letter-prepare',
      versionId,
      lineItemIds,
      strategyCodes,
    ],
    queryFn: () =>
      prepareCommitmentLetter(planId, versionId!, lineItemIds, strategyCodes),
    enabled: open && !!versionId && lineItemIds.length > 0,
    placeholderData: previous => previous,
  })

  // Prefill contacts from the plan once, without overriding edits. The due
  // date is intentionally left blank for the user to pick.
  useEffect(() => {
    const data = prepare.data
    if (!data || defaultsApplied) return
    const { getValues, setValue } = form
    const owner = data.defaults.contacts[0]
    if (owner) {
      for (const name of ['sampleContacts', 'confirmationContacts'] as const) {
        const current = getValues(name)
        if (current.length === 1 && !current[0].name && !current[0].email) {
          setValue(name, [owner])
        }
      }
    }
    setDefaultsApplied(true)
  }, [prepare.data, defaultsApplied, form])

  const {
    isProcessing,
    isCompleted,
    isError,
    progress,
    exportCommitmentLetter,
    reset: resetExport,
  } = useCommitmentLetterExport(planId, versionId ?? null)

  // Drop cached prepare/preview data so reopening reflects line item edits
  // (e.g. ship point changes) made while the wizard was closed.
  const resetWizard = useCallback(() => {
    setStep(0)
    setAcknowledgedMissing(false)
    setReviewInputs(null)
    setDefaultsApplied(false)
    form.reset(defaultFormValues())
    resetExport()
    queryClient.removeQueries({ queryKey: ['commitment-letter-prepare'] })
    queryClient.removeQueries({ queryKey: ['commitment-letter-preview'] })
  }, [form, resetExport, queryClient])

  useEffect(() => {
    if (isCompleted) {
      resetWizard()
      onOpenChange(false)
      onClearSelection()
    }
  }, [isCompleted, resetWizard, onOpenChange, onClearSelection])

  const vendors = prepare.data?.vendors ?? []
  // Vendors whose Strategy Code is not in the calendar get blank dates by
  // design (filled in manually), so they don't need "Generate anyway".
  const vendorsMissingDates = vendors.filter(
    v => v.transit_rows.length === 0 && v.strategy_has_calendar
  )
  const transitBlocked =
    !prepare.data ||
    prepare.isFetching ||
    vendors.length === 0 ||
    prepare.data.line_items_without_vendor.length > 0 ||
    (vendorsMissingDates.length > 0 && !acknowledgedMissing)

  const handleStrategyChange = (vendorKey: string, code: string | null) => {
    const next = { ...form.getValues('strategyCodes') }
    if (code) next[vendorKey] = code
    else delete next[vendorKey]
    form.setValue('strategyCodes', next)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (isProcessing && !newOpen) return
    if (!newOpen) resetWizard()
    onOpenChange(newOpen)
  }

  const parsedInputs = (): CommitmentLetterInputs | null => {
    const parsed = commitmentLetterSchema.safeParse(form.getValues())
    return parsed.success ? toInputs(parsed.data) : null
  }

  const handleContinue = async () => {
    const valid = await form.trigger(STEP_FIELDS[step])
    if (!valid) return
    if (step === TRANSIT_STEP) {
      const inputs = parsedInputs()
      if (!inputs) {
        setStep(0)
        return
      }
      setReviewInputs(inputs)
    }
    setStep(s => Math.min(s + 1, REVIEW_STEP))
  }

  const handleSubmit = () => {
    if (!reviewInputs) return
    exportCommitmentLetter(lineItemIds, reviewInputs)
  }

  const detailsBlocked =
    !prepare.data || prepare.data.line_items_without_vendor.length > 0

  const canContinue =
    step === TRANSIT_STEP
      ? !transitBlocked
      : step === 0
        ? !detailsBlocked
        : true

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-4xl flex-col gap-5">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Create Commitment Letter
          </DialogTitle>
        </DialogHeader>

        <Stepper steps={STEPS} currentStep={step} />

        {isProcessing || isError ? (
          <div className="flex flex-col gap-3 py-8">
            <Progress value={progress} />
            <p className="text-center text-sm text-muted-foreground">
              {isProcessing ? 'Generating letters...' : 'Export failed'}
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-1">
            {prepare.isError && (
              <p className="pb-3 text-sm text-destructive">
                Failed to load plan data for the commitment letter.
              </p>
            )}
            {step === 0 && (
              <StepDetails
                form={form}
                data={prepare.data}
                isLoading={prepare.isLoading}
              />
            )}
            {step === 1 && <StepContacts form={form} />}
            {step === TRANSIT_STEP && (
              <StepTransit
                data={prepare.data}
                isLoading={prepare.isFetching && !prepare.data}
                isRefreshing={prepare.isFetching}
                onStrategyChange={handleStrategyChange}
                vendorsMissingDates={vendorsMissingDates}
                acknowledgedMissing={acknowledgedMissing}
                onAcknowledgeMissing={setAcknowledgedMissing}
              />
            )}
            {step === REVIEW_STEP && versionId && reviewInputs && (
              <StepReview
                planId={planId}
                versionId={versionId}
                lineItemIds={lineItemIds}
                vendors={vendors}
                inputs={reviewInputs}
              />
            )}
          </div>
        )}

        <WizardNavigation
          onBack={() => setStep(s => Math.max(s - 1, 0))}
          canGoBack={step > 0 && !isProcessing}
          onContinue={handleContinue}
          onSubmit={handleSubmit}
          canContinue={canContinue && !isProcessing}
          isLastStep={step === REVIEW_STEP}
          isSubmitting={isProcessing}
          submitLabel="Download .ZIP"
        />
      </DialogContent>
    </Dialog>
  )
}
